import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// MUI
import { Alert, Box, Button, Container, Paper, Stack, TextField, Typography } from '@mui/material';

import { api } from 'api/client';

export default function ActivateAccount() {
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const submittingRef = useRef(false);

  const username = localStorage.getItem('user') || '';
  const signupSessionIdRaw = localStorage.getItem('signup_session_id') || '';

  // Guard against bad strings that sometimes get saved
  const signupSessionId = useMemo(() => {
    const v = String(signupSessionIdRaw).trim();
    if (!v) return '';
    if (v === 'null' || v === 'undefined') return '';
    return v;
  }, [signupSessionIdRaw]);

  useEffect(() => {
    // Redirect only if required state is missing
    if (!username || !signupSessionId) {
      navigate('/', { replace: true });
    }
  }, [navigate, username, signupSessionId]);

  const cleanupStorage = () => {
    localStorage.removeItem('token');
    // localStorage.removeItem('signup_session_id');
    localStorage.removeItem('random_code'); // legacy (safe)
    // optionally keep user
    // localStorage.removeItem('user');
  };

  const cleanCode = (v) => v.replace(/\D/g, '').slice(0, 6);

  const canSubmit = useMemo(() => {
    const c = cleanCode(code);
    return signupSessionId && c.length >= 4 && c.length <= 6 && !loading;
  }, [code, signupSessionId, loading]);

  const handleSubmit = async () => {
    if (!signupSessionId) {
      setError('Signup session missing. Please register again.');
      navigate('/', { replace: true });
      return;
    }

    if (submittingRef.current) return;
    submittingRef.current = true;

    setError('');
    setInfo('');

    const clean = cleanCode(code);
    if (clean.length < 4) {
      setError('Please enter the code sent to your mobile phone.');
      submittingRef.current = false;
      return;
    }

    setLoading(true);

    try {
      const res = await api.post(
        '/api/verify-sms/',
        { signup_session_id: signupSessionId, code: clean },
        { withCredentials: true }
      );

      const enrollToken = res?.data?.totp_enroll_token;
      if (enrollToken) localStorage.setItem('totp_enroll_token', enrollToken);

      const msg = res?.data?.detail || 'Code verified. Next: set up your authenticator app.';
      setInfo(msg);

      cleanupStorage();

      // small delay so the success message is visible
      setTimeout(() => {
        navigate('/setup-totp', { replace: true, state: { username } });
      }, 600);
    } catch (err) {
      // THIS is the key for debugging:
      console.log('VERIFY SMS ERROR', {
        status: err?.response?.status,
        data: err?.response?.data,
        raw: err?.response
      });

      const status = err?.response?.status;
      const data = err?.response?.data;

      const msg =
        data?.error ||
        data?.detail ||
        (typeof data === 'string' ? data : null) ||
        'Activation failed. Please try again.';

      setError(msg);

      // Optional: if backend says session invalid/expired, send them back to register/login
      if (status === 400 && /session|expired|missing/i.test(msg)) {
        // keep it gentle: show msg and let them decide, or auto-redirect:
        // setTimeout(() => navigate('/', { replace: true }), 1200);
      }

      // Optional: lockout handling
      if (status === 429 || /too many/i.test(msg)) {
        // setTimeout(() => navigate('/', { replace: true }), 1500);
      }
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Stack spacing={3}>
          <Typography variant="h4" fontWeight={800}>
            Multi-Factor Authentication
          </Typography>

          <Typography color="text.secondary">
            Welcome! This is a pre-activation process.
            <br />
            Verify your phone number with the code we texted you.
          </Typography>

          <Typography>
            Please enter the code sent to your <strong>mobile phone</strong> below.
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}
          {info && <Alert severity="success">{info}</Alert>}

          <TextField
            label="Activation Code"
            value={code}
            onChange={(e) => setCode(cleanCode(e.target.value))}
            inputProps={{ inputMode: 'numeric', maxLength: 6 }}
            fullWidth
          />

          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={() => navigate('/', { replace: true })} fullWidth disabled={loading}>
              Home
            </Button>

            <Button variant="contained" onClick={handleSubmit} fullWidth disabled={!canSubmit}>
              {loading ? 'Verifying…' : 'Verify Code'}
            </Button>
          </Stack>

          <Box sx={{ pt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Tip: If you didn’t receive the code, please contact support.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
}
